import { useEffect, useState } from "react";
import { useUserContext } from "../context/dashboard-context";
import { Table, Button, Badge } from "react-bootstrap";
import CustomPagination from "../components/pagination";
import api from "../context/api";
import "./orders.scss";
import { Modal, Spin, Tag, message } from "antd";
import { EnvironmentOutlined, DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';

const { confirm } = Modal;

const Orders = () => {
  const { 
    token,
    currentPage,
    setCurrentPage,
    pagesCount,
    setPagesCount,
    totalProducts,
    setTotalProducts,
    deleteOrder
  } = useUserContext();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchOrders = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get(`/orders/?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token?.access}`
        }
      });

      const ordersData = Array.isArray(response.data) 
        ? response.data 
        : response.data?.data || response.data?.results || [];
        
      setOrders(ordersData);
      
      if (response.data?.totalCount) {
        setTotalProducts(response.data.totalCount);
      } else if (response.data?.count) {
        setTotalProducts(response.data.count);
      }
      
      if (response.data?.pagesCount) {
        setPagesCount(response.data.pagesCount);
      } else if (response.data?.total_pages) {
        setPagesCount(response.data.total_pages);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      message.error("Buyurtmalarni yuklashda xatolik yuz berdi");
    } finally {
      setLoading(false);
    }
  };

  const showOrderDetails = (order) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const handleDelete = (orderId) => {
    confirm({
      title: 'Buyurtmani o\'chirishni tasdiqlaysizmi?',
      icon: <ExclamationCircleFilled />,
      content: 'Bu amalni ortga qaytarib bo\'lmaydi',
      okText: 'Ha, o\'chirish',
      okType: 'danger',
      cancelText: 'Bekor qilish',
      onOk() {
        return new Promise(async (resolve, reject) => {
          try {
            setDeletingId(orderId);
            await deleteOrder(orderId);
            await fetchOrders(currentPage);
            message.success('Buyurtma muvaffaqiyatli o\'chirildi');
            resolve();
          } catch (error) {
            message.error('Buyurtmani o\'chirishda xatolik yuz berdi');
            reject(error);
          } finally {
            setDeletingId(null);
          setIsModalVisible(false);
          setSelectedOrder(null);
          }
        });
      },
      onCancel() {
        console.log('Cancelled');
      },
    });
  };

  const renderLocation = (location) => {
    if (!location) return null;
    
    return (
      <Tag icon={<EnvironmentOutlined />} color="blue">
        {location}
      </Tag>
    );
  };

  const renderProductList = (items) => {
    if (!items || items.length === 0) {
      return <Badge bg="secondary">Mahsulotlar yo'q</Badge>;
    }
    
    return items.slice(0, 2).map((item, index) => (
      <div key={index} className="mb-1">
        <Badge bg="primary">{item.quantity || 1}x</Badge> {item.product_title || item.name || "Noma'lum mahsulot"}
      </div>
    ));
  };

  useEffect(() => {
    if (token?.access) {
      fetchOrders(currentPage);
    }
  }, [currentPage, token]);

  if (loading && orders.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="orders-page" style={{ marginTop: "30px" }}>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>Mijoz</th>
            <th>Aloqa</th>
            <th>Summa</th>
            <th>Manzil</th>
            <th>Mahsulotlar</th>
            <th>Sana</th>
            <th>Harakatlar</th>
          </tr>
        </thead>
        <tbody>
          {orders.length > 0 ? (
            orders.map(order => (
              <tr key={order.id || order._id}>
                <td>#{order.id || order._id}</td>
                <td>
                  <strong>{order.customer_name || order.user_info?.name || order.user?.name || "-"}</strong>
                  {order.customer_comment && (
                    <div className="text-muted small mt-1">
                      {order.customer_comment}
                    </div>
                  )}
                </td>
                <td>
                  <a href={`tel:${order.customer_phone || order.user_info?.phone || order.user?.phone}`}>
                    {order.customer_phone || order.user_info?.phone || order.user?.phone || "-"}
                  </a>
                </td>
                <td className="text-nowrap">{order.total_price || order.total || 0} so'm</td>
                <td>
                  {renderLocation(order.customer_location || order.user_info?.location || order.shipping_address)}
                </td>
                <td>
                  {renderProductList(order.items || order.products || [])}
                  {(order.items?.length > 2 || order.products?.length > 2) && (
                    <div className="mt-1">
                      <Badge bg="info">
                        + {(order.items?.length || order.products?.length || 0) - 2} ta
                      </Badge>
                    </div>
                  )}
                </td>
                <td className="text-nowrap">
                  {new Date(order.created_at || order.date || order.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => showOrderDetails(order)}
                    >
                      Batafsil
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(order.id || order._id)}
                      disabled={deletingId === (order.id || order._id)}
                    >
                      {deletingId === (order.id || order._id) ? (
                        <Spin size="small" />
                      ) : (
                        <>
                          <DeleteOutlined /> O'chirish
                        </>
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" style={{ textAlign: "center" }}>
                {loading ? 'Yuklanmoqda...' : 'Buyurtmalar topilmadi.'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {orders.length > 0 && (
        <CustomPagination
          current={currentPage}
          total={totalProducts}
          pageSize={10}
          onChange={(page) => {
            setCurrentPage(page);
            fetchOrders(page);
          }}
        />
      )}

      <Modal
        title={`Buyurtma #${selectedOrder?.id || selectedOrder?._id}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedOrder(null);
        }}
        footer={null}
        width={800}
      >
        {selectedOrder && (
          <div className="order-details">
            <div className="row mb-4">
              <div className="col-md-6">
                <h5>Mijoz Ma'lumotlari</h5>
                <p><strong>Ismi:</strong> {selectedOrder.customer_name || selectedOrder.user_info?.name || selectedOrder.user?.name || "-"}</p>
                <p><strong>Telefon:</strong> {selectedOrder.customer_phone || selectedOrder.user_info?.phone || selectedOrder.user?.phone || "-"}</p>
                <p><strong>Manzil:</strong> {selectedOrder.customer_location || selectedOrder.user_info?.location || selectedOrder.shipping_address || "Ko'rsatilmagan"}</p>
              </div>
              <div className="col-md-6">
                <h5>Buyurtma Ma'lumotlari</h5>
                <p><strong>Sana:</strong> {new Date(selectedOrder.created_at || selectedOrder.createdAt).toLocaleString()}</p>
                <p><strong>Jami summa:</strong> {selectedOrder.total_price || selectedOrder.total || 0} so'm</p>
                <p><strong>Izoh:</strong> {selectedOrder.customer_comment || selectedOrder.notes || selectedOrder.comment || "Yo'q"}</p>
              </div>
            </div>

            <h5>Buyurtma qilingan mahsulotlar</h5>
            {(selectedOrder.items || selectedOrder.products || []).length > 0 ? (
              <>
                <Table bordered className="mb-4">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Mahsulot</th>
                      <th>Kategoriya</th>
                      <th>Narx</th>
                      <th>Soni</th>
                      <th>Summa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedOrder.items || selectedOrder.products || []).map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <div>{item.product_title || item.name || "Noma'lum mahsulot"}</div>
                          {(item.product_snapshot?.title_uz || item.title_uz) && (
                            <small className="text-muted">
                              ({item.product_snapshot?.title_uz || item.title_uz})
                            </small>
                          )}
                        </td>
                        <td>{item.product_snapshot?.category || item.category || "-"}</td>
                        <td>{item.price || 0} so'm</td>
                        <td>{item.quantity || 1}</td>
                        <td>{(item.price || 0) * (item.quantity || 1)} so'm</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    {(selectedOrder.customer_location || selectedOrder.shipping_address) && (
                      <div className="mb-2">
                        <strong>Yetkazib berish manzili:</strong>
                        <div className="p-2 bg-light rounded mt-1">
                          <EnvironmentOutlined className="me-2" />
                          {selectedOrder.customer_location || selectedOrder.shipping_address}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-end">
                    <h4>Jami: {selectedOrder.total_price || selectedOrder.total || 0} so'm</h4>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-center py-3">Bu buyurtmada mahsulot yo'q.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Orders;